/**
 * AIチャットビューコンポーネント
 */

import { Bot, Send, X } from 'lucide-react'
import { useCallback, useRef, useState } from 'react'

import { sendJournalChat } from '@/api/services/journal'
import type { InterviewQuestion } from '@/api/types/journal'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'

type ChatMessage = {
  role: 'user' | 'ai'
  content: string
}

type Props = {
  question: InterviewQuestion
  dateStr: string
  onClose: () => void
}

export const ChatView = ({ question, dateStr, onClose }: Props) => {
  const [messages, setMessages] = useState<ChatMessage[]>([{ role: 'ai', content: question.question }])
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [parentNoteId, setParentNoteId] = useState<number | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)

  const handleSend = useCallback(async () => {
    const text = input.trim()
    if (!text || isLoading) return

    setInput('')
    setMessages((prev) => [...prev, { role: 'user', content: text }])
    setIsLoading(true)

    try {
      const response = await sendJournalChat({
        message: text,
        parent_note_id: parentNoteId,
        date: dateStr,
      })
      // 初回送信時に親ノートIDを保存
      if (!parentNoteId) {
        setParentNoteId(response.user_note_id)
      }
      setMessages((prev) => [...prev, { role: 'ai', content: response.reply }])
    } catch {
      setMessages((prev) => [...prev, { role: 'ai', content: 'エラーが発生しました。もう一度お試しください。' }])
    } finally {
      setIsLoading(false)
      setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' }), 100)
    }
  }, [input, isLoading, parentNoteId, dateStr])

  return (
    <div className="flex flex-col h-full">
      {/* ヘッダー */}
      <div className="flex items-center justify-between px-4 py-2 border-b bg-muted/30">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Bot className="size-4 text-primary" />
          <span className="truncate">{question.category}</span>
        </div>
        <Button variant="ghost" size="icon" className="size-7" onClick={onClose}>
          <X className="size-4" />
        </Button>
      </div>

      {/* メッセージ一覧 */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div
              className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${
                msg.role === 'user' ? 'bg-primary text-primary-foreground rounded-br-sm' : 'bg-muted rounded-bl-sm'
              }`}
            >
              {msg.content}
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-muted rounded-2xl rounded-bl-sm px-3 py-2 text-sm">
              <span className="animate-pulse">考え中...</span>
            </div>
          </div>
        )}
      </div>

      {/* 入力エリア */}
      <div className="border-t p-3">
        <div className="flex gap-2">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                handleSend()
              }
            }}
            placeholder="メッセージを入力..."
            className="min-h-[40px] max-h-[120px] resize-none"
            rows={1}
          />
          <Button size="icon" onClick={handleSend} disabled={!input.trim() || isLoading}>
            <Send className="size-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}
